import React, { useState, useRef } from 'react';
import { Button } from 'system/atoms';
import { Box, IconButton, Stack, SxProps, Theme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FieldProps, DropdownFieldSchema } from '../types/Form';
import { FieldWrapper } from './FieldWrapper';
import { getNewOptionValues } from 'common/form/utils/getNewOptionValues';
import { TableV2 } from 'components/Table/TableV2';
import { TrashIcon } from 'components/Icons/TrashIcon';
import { useConfirmationModal } from 'services/modals/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DropdownList } from 'system';
import { SubLabelWithClearLink } from './SubLabelWithClearLink';

const dropdownContentSx: SxProps<Theme> = {
  backgroundColor: (theme) => theme.palette.common.white,
  justifyContent: 'space-between',
  fontWeight: 'normal',
  borderRadius: '8px'
};

export const Dropdown: React.FC<FieldProps<DropdownFieldSchema>> = ({
  name,
  value,
  field,
  errorMessage,
  error,
  onChange
}) => {
  const {
    label,
    subLabel,
    containerSx,
    options,
    isSearchable = false,
    isLoading = false,
    stickyButtonEndComponent,
    onRefresh,
    onSearchChange,
    searchPlaceholder,
    hasList = false,
    listOptions,
    isMulti = false,
    buttonText,
    isClearable = false,
    clearLabel,
    popperSx
  } = field;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { showModal } = useConfirmationModal();

  // Convert value to array for multi-select
  const selectedValues = Array.isArray(value) ? value : [value];

  // Find the currently selected option
  const selectedOptions = options.filter((opt) =>
    selectedValues.includes(opt.value)
  );

  // Filter options based on search query
  const filteredOptions = options.filter((option) =>
    (option.label as string).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    const newOptionValues = getNewOptionValues(
      optionValue,
      selectedValues,
      isMulti
    );
    if (!isMulti) {
      onChange(newOptionValues);
      setIsOpen(false);
      setSearchQuery('');
    } else {
      onChange(newOptionValues);
    }
  };

  const removeOption = (optionValue: string) => {
    if (isMulti) {
      const newValue = selectedValues.filter((val) => val !== optionValue);
      onChange(newValue);
    } else {
      onChange('');
    }
  };

  const handleDelete = (optionValue: string) => {
    if (listOptions?.destructiveModal) {
      showModal({
        title:
          listOptions?.destructiveModalOptions?.destructiveTitle ??
          'Are you sure?',
        message:
          listOptions?.destructiveModalOptions?.destructiveMessage ??
          'This option will be deleted from the list',
        modalType: ConfirmationModalType.DESTRUCTIVE,
        destructiveText:
          listOptions?.destructiveModalOptions?.destructiveText ?? 'OK',
        onDestructiveConfirm: () => {
          removeOption(optionValue);
        }
      });
      return;
    }
    removeOption(optionValue);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const getButtonText = () => {
    if (!isMulti) {
      return selectedOptions[0]?.label ?? buttonText ?? 'Choose';
    }

    if (selectedOptions.length === 0) {
      return buttonText ?? 'Choose';
    }

    return `Choose (${selectedOptions.length})`;
  };

  return (
    <FieldWrapper
      label={label}
      subLabel={
        <SubLabelWithClearLink
          subLabel={subLabel}
          isClearable={isClearable}
          onClearClicked={() => onChange([])}
          clearLabel={clearLabel}
        />
      }
      errorMessage={errorMessage}
      sx={containerSx}
    >
      {(options.length !== 0 || !!buttonText) && (
        <Stack direction="row" justifyItems="center" gap={1}>
          <Button
            ref={anchorRef}
            data-testid={`dropdown-${name}`}
            fullWidth
            variant="secondary"
            type="button"
            state={errorMessage || error ? 'destructive' : 'hover'}
            endIcon={<KeyboardArrowDownIcon />}
            loading={isLoading}
            loadingType="spinner"
            sx={dropdownContentSx}
            onClick={() => setIsOpen(!isOpen)}
          >
            {getButtonText()}
          </Button>
          {onRefresh && (
            <IconButton
              onClick={onRefresh}
              disabled={isLoading}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          )}
        </Stack>
      )}

      <DropdownList
        popperProps={{
          open: isOpen,
          anchorEl: anchorRef.current,
          placement: 'bottom-start',
          style: { width: anchorRef.current?.offsetWidth, zIndex: 1300 },
          sx: popperSx
        }}
        options={filteredOptions}
        searchOptions={
          isSearchable
            ? {
                placeholder: searchPlaceholder,
                searchQuery,
                onChange: (query: string) => {
                  setSearchQuery(query);
                  onSearchChange?.(query);
                }
              }
            : undefined
        }
        selectedValues={selectedValues}
        stickyButton={
          stickyButtonEndComponent && {
            text: stickyButtonEndComponent.text,
            startIcon: stickyButtonEndComponent.startIcon,
            onClick: () => {
              handleClose();
              stickyButtonEndComponent.onClick();
            }
          }
        }
        onSelect={handleSelect}
        onClickAway={handleClose}
      />
      {hasList && selectedOptions.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TableV2
            noTableHead={true}
            columns={[
              {
                name: 'Label',
                cell: (row) => row.label
              },
              {
                name: 'Actions',
                cell: (row) => (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      backgroundColor: 'transparent'
                    }}
                  >
                    <IconButton
                      data-testid={`delete-option-${row.value}`}
                      sx={{
                        stroke: 'error.main'
                      }}
                      aria-label="delete"
                      onClick={() => handleDelete(row.value)}
                    >
                      <TrashIcon type="error" />
                    </IconButton>
                  </Box>
                )
              }
            ]}
            data={selectedOptions.map((option) => ({
              label: option.label,
              value: option.value
            }))}
          />
        </Box>
      )}
    </FieldWrapper>
  );
};
